import type { IBusiness } from "@/business/business";
import { hasAiScriptAccess } from "@/core/business/payroll-only";
import { useCanViewPayrollRoster } from "@/payroll/roster/permissions";
import { mayAnswerPackQuestions } from "@/user/utils";
import { usePayPlanAccess } from "./pay-plan-access";
import {
  usePaypacksAccess,
  usePayrollEmailsAccess,
} from "./payroll-emails-access";
import {
  getStAdjustmentsAccess,
  useStAdjustmentsProductAccess,
} from "./st-adjustments/access";
import { ST_ADJUSTMENTS_ACCESS } from "./st-adjustments/types";
import { PAYROLL_EMAILS_ACCESS, PAY_BREAKDOWN_SECTION } from "./types";
import {
  hasFullWorkbookAuthority,
  type WorkbookAuthorityUser,
} from "./workbook-authority";

export const usePayrollSectionAccess = ({
  business,
  user,
  isAiScript,
}: {
  business: IBusiness | null | undefined;
  user: WorkbookAuthorityUser | null;
  isAiScript: boolean;
}) => {
  const roster = useCanViewPayrollRoster(business, user);
  const payPlan = usePayPlanAccess();
  const emails = usePayrollEmailsAccess();
  const paypacks = usePaypacksAccess();
  const adjustments = useStAdjustmentsProductAccess();
  const stAdjustmentsAccess = getStAdjustmentsAccess({
    productAccess: adjustments.access,
    serviceTitanEnabled: !!business?.service_titan_enabled,
    isAiScript,
    hasFullWorkbookAccess: hasFullWorkbookAuthority(business, user),
  });
  const hasAiScript = hasAiScriptAccess(business);
  const canViewPayrollEmails =
    emails.payrollEmailsAccess !== PAYROLL_EMAILS_ACCESS.hidden;
  const canViewPackChat = paypacks.enabled && mayAnswerPackQuestions(user);
  const hasStAdjustmentsAccess =
    stAdjustmentsAccess !== ST_ADJUSTMENTS_ACCESS.hidden;
  const showSectionTabs =
    hasAiScript &&
    isAiScript &&
    (roster.canViewRoster ||
      payPlan.canViewPayPlan ||
      canViewPayrollEmails ||
      canViewPackChat ||
      hasStAdjustmentsAccess);

  return {
    hasAiScript,
    canViewRoster: roster.canViewRoster,
    canViewPayPlan: payPlan.canViewPayPlan,
    payrollEmailsAccess: emails.payrollEmailsAccess,
    canViewPackChat,
    stAdjustmentsAccess,
    showSectionTabs,
    availability: {
      [PAY_BREAKDOWN_SECTION.breakdown]: true,
      [PAY_BREAKDOWN_SECTION.roster]: showSectionTabs && roster.canViewRoster,
      [PAY_BREAKDOWN_SECTION.payPlan]:
        showSectionTabs && payPlan.canViewPayPlan,
      [PAY_BREAKDOWN_SECTION.packs]: showSectionTabs && canViewPayrollEmails,
      [PAY_BREAKDOWN_SECTION.packChat]: showSectionTabs && canViewPackChat,
      [PAY_BREAKDOWN_SECTION.adjustments]:
        showSectionTabs && hasStAdjustmentsAccess,
    },
    isLoading:
      roster.isLoading ||
      payPlan.isLoading ||
      emails.isLoading ||
      paypacks.isLoading ||
      adjustments.isLoading,
  };
};
